import React, { useEffect, useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { User, Phone, Calendar, Edit3, Lock, Download, ShoppingBag, CheckCircle, XCircle, Clock, Save, Eye, EyeOff, FileText, CreditCard, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Separator } from '../components/ui/Separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { Footer } from '../components/Footer';
import { toast } from 'sonner';
export function Profile() {
  const {
    t
  } = useLanguage();
  const {
    currentUser,
    updateProfile,
    changePassword
  } = useAuth();
  const {
    files,
    payments,
    getFileData
  } = useData();
  const navigate = useNavigate();
  // Edit name state
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [savingName, setSavingName] = useState(false);
  // Change password state
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  useEffect(() => {
    if (!currentUser) navigate('/auth');
  }, [currentUser, navigate]);
  useEffect(() => {
    if (currentUser) setNewName(currentUser.fullName);
  }, [currentUser]);
  if (!currentUser) return null;
  const userPayments = payments.filter(p => p.userId === currentUser.id);
  const purchasedFiles = files.filter(f => currentUser.purchasedFiles.includes(f.id));
  const pendingPayments = userPayments.filter(p => p.status === 'pending');
  const verifiedPayments = userPayments.filter(p => p.status === 'verified');
  const deniedPayments = userPayments.filter(p => p.status === 'denied');
  const handleSaveName = async () => {
    if (!newName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    setSavingName(true);
    const result = await updateProfile(newName.trim());
    if (result.success) {
      toast.success('Name updated successfully!');
      setEditingName(false);
    } else {
      toast.error(result.error || 'Failed to update name');
    }
    setSavingName(false);
  };
  const handleChangePassword = async () => {
    if (!oldPassword) {
      toast.error('Enter your current password');
      return;
    }
    if (newPassword.length < 4) {
      toast.error('New password must be at least 4 characters');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error('New passwords do not match');
      return;
    }
    setSavingPassword(true);
    const result = await changePassword(oldPassword, newPassword);
    if (result.success) {
      toast.success('Password changed successfully!');
      setOldPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } else {
      toast.error(result.error || 'Failed to change password');
    }
    setSavingPassword(false);
  };
  const handleDownload = async (file: {
    id: string;
    name: string;
    fileFormat: string;
  }) => {
    toast.info(`Preparing ${file.name}...`);
    const data = await getFileData(file.id);
    if (data) {
      const a = document.createElement('a');
      a.href = data;
      a.download = `${file.name}.${file.fileFormat.toLowerCase()}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success(`Downloading ${file.name}`);
    } else {
      const blob = new Blob([`${file.name}\nFormat: ${file.fileFormat}\n\nThank you for purchasing from Theo EduMarket Rwanda!`], {
        type: 'text/plain'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${file.name}.${file.fileFormat.toLowerCase()}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloading ${file.name}`);
    }
  };
  return <div className="w-full min-h-screen flex flex-col" data-app="theo-edumarket" data-id="element-1034">
      <div className="flex-1 py-6 px-4" data-id="element-1035">
        <div className="mx-auto max-w-3xl space-y-6" data-id="element-1036">
          {/* Profile Header */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} data-id="element-1037">
            <Card data-id="element-1038">
              <CardContent className="p-6" data-id="element-1039">
                <div className="flex items-start gap-4" data-id="element-1040">
                  <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center flex-shrink-0" data-id="element-1041">
                    <User className="h-8 w-8 text-white" data-id="element-1042" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1043">
                    {editingName ? <div className="flex items-center gap-2" data-id="element-1044">
                        <Input value={newName} onChange={e => setNewName(e.target.value)} className="max-w-[250px]" autoFocus data-id="element-1045" />
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white gap-1" onClick={handleSaveName} disabled={savingName} data-id="element-1046">
                          <Save className="h-3.5 w-3.5" data-id="element-1047" />
                          {savingName ? '...' : 'Save'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => {
                      setEditingName(false);
                      setNewName(currentUser.fullName);
                    }} data-id="element-1048">
                          Cancel
                        </Button>
                      </div> : <div className="flex items-center gap-2" data-id="element-1049">
                        <h2 className="text-xl font-bold truncate" data-id="element-1050">
                          {currentUser.fullName}
                        </h2>
                        <Button variant="ghost" size="icon-xs" onClick={() => setEditingName(true)} className="text-muted-foreground hover:text-foreground" data-id="element-1051">
                          <Edit3 className="h-3.5 w-3.5" data-id="element-1052" />
                        </Button>
                      </div>}
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground" data-id="element-1053">
                      <span className="flex items-center gap-1" data-id="element-1054">
                        <Phone className="h-3.5 w-3.5" data-id="element-1055" />
                        {currentUser.countryCode}
                        {currentUser.phone}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-1056">
                        <Calendar className="h-3.5 w-3.5" data-id="element-1057" />
                        Joined{' '}
                        {new Date(currentUser.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3" data-id="element-1058">
                      <Badge className="bg-blue-600 text-white gap-1" data-id="element-1059">
                        <ShoppingBag className="h-3 w-3" data-id="element-1060" />
                        {purchasedFiles.length} purchased
                      </Badge>
                      {pendingPayments.length > 0 && <Badge variant="secondary" className="gap-1" data-id="element-1061">
                          <Clock className="h-3 w-3" data-id="element-1062" />
                          {pendingPayments.length} pending
                        </Badge>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Tabs */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} data-id="element-1063">
            <Tabs defaultValue="purchases" data-id="element-1064">
              <TabsList className="w-full grid grid-cols-3" data-id="element-1065">
                <TabsTrigger value="purchases" className="gap-1.5 text-xs sm:text-sm" data-id="element-1066">
                  <Download className="h-3.5 w-3.5" data-id="element-1067" />
                  <span className="hidden sm:inline" data-id="element-1068">My</span> Downloads
                </TabsTrigger>
                <TabsTrigger value="payments" className="gap-1.5 text-xs sm:text-sm" data-id="element-1069">
                  <CreditCard className="h-3.5 w-3.5" data-id="element-1070" />
                  Payments
                </TabsTrigger>
                <TabsTrigger value="security" className="gap-1.5 text-xs sm:text-sm" data-id="element-1071">
                  <Lock className="h-3.5 w-3.5" data-id="element-1072" />
                  Security
                </TabsTrigger>
              </TabsList>

              {/* PURCHASES TAB */}
              <TabsContent value="purchases" data-id="element-1073">
                <Card data-id="element-1074">
                  <CardHeader data-id="element-1075">
                    <CardTitle className="text-base flex items-center gap-2" data-id="element-1076">
                      <Download className="h-5 w-5 text-blue-600" data-id="element-1077" />
                      My Purchased Files ({purchasedFiles.length})
                    </CardTitle>
                    <CardDescription data-id="element-1078">
                      Files you've purchased and can download
                    </CardDescription>
                  </CardHeader>
                  <CardContent data-id="element-1079">
                    {purchasedFiles.length === 0 ? <div className="text-center py-12" data-id="element-1080">
                        <ShoppingBag className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" data-id="element-1081" />
                        <p className="text-sm text-muted-foreground mb-3" data-id="element-1082">
                          No purchases yet
                        </p>
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white gap-2" onClick={() => navigate('/marketplace')} data-id="element-1083">
                          <ShoppingBag className="h-4 w-4" data-id="element-1084" />
                          Browse Marketplace
                        </Button>
                      </div> : <div className="space-y-3" data-id="element-1085">
                        {purchasedFiles.map(file => <motion.div key={file.id} initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors" data-id="element-1086">
                            <img src={file.coverUrl} alt={file.name} className="h-14 w-14 rounded-lg object-cover bg-muted flex-shrink-0" onError={e => {
                        ;
                        (e.target as HTMLImageElement).src = `https://picsum.photos/seed/${file.id}/56/56`;
                      }} data-id="element-1087" />
                            <div className="flex-1 min-w-0" data-id="element-1088">
                              <p className="font-medium text-sm truncate" data-id="element-1089">
                                {file.name}
                              </p>
                              <div className="flex items-center gap-2 mt-1" data-id="element-1090">
                                <Badge variant="outline" className="text-[10px] h-5" data-id="element-1091">
                                  {file.fileFormat}
                                </Badge>
                                <Badge variant="secondary" className="text-[10px] h-5" data-id="element-1092">
                                  {file.type}
                                </Badge>
                                <span className="text-xs text-muted-foreground" data-id="element-1093">
                                  {file.price.toLocaleString()} FRW
                                </span>
                              </div>
                            </div>
                            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white gap-1.5 flex-shrink-0" onClick={() => handleDownload(file)} data-id="element-1094">
                              <Download className="h-3.5 w-3.5" data-id="element-1095" />
                              <span className="hidden sm:inline" data-id="element-1096">Download</span>
                            </Button>
                          </motion.div>)}
                      </div>}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* PAYMENTS TAB */}
              <TabsContent value="payments" data-id="element-1097">
                <Card data-id="element-1098">
                  <CardHeader data-id="element-1099">
                    <CardTitle className="text-base flex items-center gap-2" data-id="element-1100">
                      <CreditCard className="h-5 w-5 text-blue-600" data-id="element-1101" />
                      Payment History ({userPayments.length})
                    </CardTitle>
                    <CardDescription data-id="element-1102">
                      All your payment requests and their status
                    </CardDescription>
                  </CardHeader>
                  <CardContent data-id="element-1103">
                    {userPayments.length === 0 ? <div className="text-center py-12" data-id="element-1104">
                        <CreditCard className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" data-id="element-1105" />
                        <p className="text-sm text-muted-foreground" data-id="element-1106">
                          No payment history
                        </p>
                      </div> : <div className="space-y-3" data-id="element-1107">
                        {[...userPayments].reverse().map(payment => <div key={payment.id} className="p-3 rounded-lg border border-border space-y-2" data-id="element-1108">
                            <div className="flex items-start justify-between" data-id="element-1109">
                              <div data-id="element-1110">
                                <p className="font-medium text-sm" data-id="element-1111">
                                  {payment.fileName}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5" data-id="element-1112">
                                  {new Date(payment.createdAt).toLocaleDateString()}{' '}
                                  at{' '}
                                  {new Date(payment.createdAt).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                                </p>
                              </div>
                              <Badge className={payment.status === 'verified' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' : payment.status === 'denied' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'} data-id="element-1113">
                                {payment.status === 'verified' && <CheckCircle className="h-3 w-3 mr-1" data-id="element-1114" />}
                                {payment.status === 'denied' && <XCircle className="h-3 w-3 mr-1" data-id="element-1115" />}
                                {payment.status === 'pending' && <Clock className="h-3 w-3 mr-1" data-id="element-1116" />}
                                {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                              </Badge>
                            </div>
                            <Separator data-id="element-1117" />
                            <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-1118">
                              <div data-id="element-1119">
                                <span className="text-muted-foreground" data-id="element-1120">
                                  Amount:
                                </span>{' '}
                                <strong data-id="element-1121">
                                  {payment.amount.toLocaleString()} FRW
                                </strong>
                              </div>
                              <div data-id="element-1122">
                                <span className="text-muted-foreground" data-id="element-1123">
                                  MTN:
                                </span>{' '}
                                {payment.payerMtnNumber}
                              </div>
                              <div data-id="element-1124">
                                <span className="text-muted-foreground" data-id="element-1125">
                                  Payer:
                                </span>{' '}
                                {payment.payerName}
                              </div>
                              {payment.resolvedAt && <div data-id="element-1126">
                                  <span className="text-muted-foreground" data-id="element-1127">
                                    Resolved:
                                  </span>{' '}
                                  {new Date(payment.resolvedAt).toLocaleDateString()}
                                </div>}
                            </div>
                            {payment.feedback && <p className="text-xs text-red-500 bg-red-50 dark:bg-red-900/10 rounded p-2" data-id="element-1128">
                                {payment.feedback}
                              </p>}
                          </div>)}
                      </div>}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* SECURITY TAB */}
              <TabsContent value="security" data-id="element-1129">
                <Card data-id="element-1130">
                  <CardHeader data-id="element-1131">
                    <CardTitle className="text-base flex items-center gap-2" data-id="element-1132">
                      <Lock className="h-5 w-5 text-blue-600" data-id="element-1133" />
                      Change Password
                    </CardTitle>
                    <CardDescription data-id="element-1134">
                      Update your account password
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4" data-id="element-1135">
                    <div className="space-y-2" data-id="element-1136">
                      <Label data-id="element-1137">Current Password</Label>
                      <div className="relative" data-id="element-1138">
                        <Input type={showPasswords ? 'text' : 'password'} value={oldPassword} onChange={e => setOldPassword(e.target.value)} placeholder="Enter current password" className="pr-10" data-id="element-1139" />
                        <button type="button" onClick={() => setShowPasswords(!showPasswords)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-id="element-1140">
                          {showPasswords ? <EyeOff className="h-4 w-4" data-id="element-1141" /> : <Eye className="h-4 w-4" data-id="element-1142" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2" data-id="element-1143">
                      <Label data-id="element-1144">New Password</Label>
                      <Input type={showPasswords ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Enter new password (min 4 chars)" data-id="element-1145" />
                    </div>
                    <div className="space-y-2" data-id="element-1146">
                      <Label data-id="element-1147">Confirm New Password</Label>
                      <Input type={showPasswords ? 'text' : 'password'} value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} placeholder="Confirm new password" data-id="element-1148" />
                    </div>
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2" onClick={handleChangePassword} disabled={savingPassword} data-id="element-1149">
                      <Lock className="h-4 w-4" data-id="element-1150" />
                      {savingPassword ? 'Changing...' : 'Change Password'}
                    </Button>
                  </CardContent>
                </Card>

                {/* Account Info */}
                <Card className="mt-4" data-id="element-1151">
                  <CardHeader data-id="element-1152">
                    <CardTitle className="text-base" data-id="element-1153">Account Info</CardTitle>
                  </CardHeader>
                  <CardContent data-id="element-1154">
                    <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-1155">
                      <div className="text-muted-foreground" data-id="element-1156">User ID:</div>
                      <div className="font-mono text-xs truncate" data-id="element-1157">
                        {currentUser.id}
                      </div>
                      <div className="text-muted-foreground" data-id="element-1158">Phone:</div>
                      <div data-id="element-1159">
                        {currentUser.countryCode}
                        {currentUser.phone}
                      </div>
                      <div className="text-muted-foreground" data-id="element-1160">Joined:</div>
                      <div data-id="element-1161">
                        {new Date(currentUser.createdAt).toLocaleDateString()}
                      </div>
                      <div className="text-muted-foreground" data-id="element-1162">Password:</div>
                      <div data-id="element-1163">
                        <Badge variant="secondary" className="text-[10px]" data-id="element-1164">
                          {currentUser.passwordHash.startsWith('sh_') ? '🔒 Strong (salted)' : '⚠️ Legacy (will upgrade on next login)'}
                        </Badge>
                      </div>
                      <div className="text-muted-foreground" data-id="element-1165">Purchases:</div>
                      <div data-id="element-1166">{currentUser.purchasedFiles.length} files</div>
                    </div>
                  </CardContent>
                </Card>

                {/* WhatsApp Support */}
                <Card className="mt-4" data-id="element-1167">
                  <CardContent className="p-4" data-id="element-1168">
                    <a href="https://wa.me/250783138128?text=Hello%20Theo%20EduMarket%20Rwanda%2C%20I%20need%20help" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm hover:bg-muted/50 rounded-lg p-2 -m-2 transition-colors" data-id="element-1169">
                      <div className="h-10 w-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0" data-id="element-1170">
                        <MessageSquare className="h-5 w-5 text-white" data-id="element-1171" />
                      </div>
                      <div data-id="element-1172">
                        <p className="font-medium" data-id="element-1173">Need Help?</p>
                        <p className="text-xs text-muted-foreground" data-id="element-1174">
                          Contact admin on WhatsApp: 0783138128
                        </p>
                      </div>
                    </a>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      </div>
      <Footer data-id="element-1175" />
    </div>;
}